import {
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { category } from "./state_management/actionTypes";
import * as Sentry from "@sentry/react";
import { changeLocale, setOrderDetails } from "./state_management/actions";
import { stateType, store } from "./store";
import { json_key, locale, translate, translate_options } from "./translate";
import { getCartTotal } from "./utilities/cartUtilities";
import { getDefaultReturn, useAsync } from "./utilities/asyncFunc";
import { getAllCategories } from "./state_management/epics/services";
import { cartItem, extrasCartItem } from "./state_management/reducers/cart";
import { useCoupon } from "./utilities/useCheckoutCoupon";
import { orderType } from "./state_management/reducers/order";
import { connect } from "react-redux";
import { getBenefitBarHTML, getOrderPrice } from "./state_management/epics";
import { useDeepEffect } from "./utilities/useDeepEffect";
import {
  barCacheKey,
  retrieveData,
  wordpressBarResponse
} from "./state_management/epics/settings";

export const CartTotalContext = createContext<[number, (data: number) => void]>(
  [0, () => {}]
);

export const CategoryContext = createContext<
  [category[], (data: category[]) => void]
>([[], () => {}]);

export const LocaleContext = createContext<{
  locale: locale;
  changeLocale: (locale: locale) => void;
  t: (originalText: json_key, options?: translate_options) => string;
}>({
  changeLocale,
  locale: store.getState().settingsReducer.locale,
  t: translate
});

export const TotalWithCouponContext = createContext<
  [number, (data: number) => void]
>([0, (data: number) => {}]);

export type couponObjReturnType = ReturnType<typeof useCoupon>;
export const CouponObjectContext = createContext<{
  setCouponParams: React.Dispatch<Parameters<typeof useCoupon>[0]>;
  CouponObject: couponObjReturnType;
}>({
  setCouponParams: () => {},
  CouponObject: {
    // @ts-ignore
    couponRes: getDefaultReturn({
      coupon_code: null,
      extra_services: [],
      gift_values: [],
      services: []
    } as Parameters<typeof getOrderPrice>[0]),
    localCouponValue: null,
    setShowDiscount: () => {},
    showDiscount: false,
    couponError: false,
    couponErrorType: undefined,
    isApplied: false,
    setLocalCouponValue: () => {},
    disabledCouponInput: false,
    reset: () => {}
  }
});

const WordPressBarContext = createContext<wordpressBarResponse | null>(null);

const AppProvider = ({
  children,
  cart,
  extras,
  locale,
  changeLocale
}: {
  children: ReactElement;
  cart: cartItem[];
  extras: extrasCartItem[];
  locale: locale;
  changeLocale: (locale: locale) => void;
}) => {
  const [cartTotal, setCartTotal] = useState(0);
  const [categories, setCategories] = useState<category[]>([]);
  const [totalWithCoupon, setTotalWithCoupon] = useState(0);
  const bars = retrieveData<wordpressBarResponse>(barCacheKey);

  const total = useMemo(() => {
    return getCartTotal({
      raw_cart: { cart, extras }
    });
  }, [JSON.stringify({ cart, extras })]);

  useEffect(() => {
    setCartTotal(total);
  }, [total]);

  const res = useAsync({
    asyncFunc: getAllCategories,
    funcParams: {},
    immediate: true
  });

  useAsync({
    asyncFunc: getAllCategories,
    funcParams: { background: true },
    immediate: true
  });

  useEffect(() => {
    if (res.data) {
      setCategories([...res.data]);
    }
  }, [res.data]);

  const barRes = useAsync(
    {
      asyncFunc: getBenefitBarHTML,
      funcParams: {},
      immediate: true
    },
    []
  );

  // Background
  useAsync(
    {
      asyncFunc: getBenefitBarHTML,
      funcParams: { background: true },
      immediate: true
    },
    []
  );

  const htmlObject = useMemo((): wordpressBarResponse | null => {
    if (!barRes.data) {
      return bars;
    } else {
      return barRes.data;
    }
  }, [bars, barRes.data]);

  return (
    <Sentry.ErrorBoundary>
      <LocaleContext.Provider value={{ changeLocale, locale, t: translate }}>
        <WordPressBarContext.Provider value={htmlObject}>
          <CartTotalContext.Provider value={[cartTotal, setCartTotal]}>
            <CategoryContext.Provider value={[categories, setCategories]}>
              <TotalWithCouponContext.Provider
                value={[totalWithCoupon, setTotalWithCoupon]}
              >
                <CouponProvider>{children}</CouponProvider>
              </TotalWithCouponContext.Provider>
            </CategoryContext.Provider>
          </CartTotalContext.Provider>
        </WordPressBarContext.Provider>
      </LocaleContext.Provider>
    </Sentry.ErrorBoundary>
  );
};

const mapStateToProps = (state: stateType) => ({
  cart: state.cartReducer.cart,
  extras: state.cartReducer.extras,
  orderDetails: state.orderReducer.details
});

const mapDispatchToProps = (dispatch: any) => ({
  setOrderDetails: (data: orderType) => {
    dispatch(setOrderDetails(data));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(AppProvider);

const CouponProvider = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  ({
    children,
    cart,
    extras,
    orderDetails,
    setOrderDetails
  }: {
    children: React.ReactNode;
    cart: cartItem[];
    extras: extrasCartItem[];
    orderDetails: orderType;
    setOrderDetails: (data: orderType) => void;
  }) => {
    const [couponParams, setCouponParams] = useState({
      cart,
      extras,
      direct: false,
      inactive: false,
      orderDetails,
      setOrderDetails
    });

    useEffect(() => {
      setCouponParams({
        ...couponParams,
        cart,
        extras,
        orderDetails,
        setOrderDetails
      });
    }, [cart, extras, orderDetails, setOrderDetails]);

    const CouponObject = useCoupon(couponParams);

    return (
      <CouponObjectContext.Provider value={{ setCouponParams, CouponObject }}>
        {children}
      </CouponObjectContext.Provider>
    );
  }
);

export const useCouponParams = (
  couponParams: Parameters<typeof useCoupon>[0]
) => {
  const { setCouponParams } = useContext(CouponObjectContext);
  useDeepEffect(() => {
    setCouponParams({
      ...couponParams
    });
  }, [couponParams]);
};

export const useWordPressBar = (): {
  topBar: wordpressBarResponse["data"]["bars"][number] | null;
  benefitBar: wordpressBarResponse["data"]["bars"][number] | null;
} => {
  const barData = useContext(WordPressBarContext);

  return {
    topBar: barData?.data.bars.find((bar) => bar.name === "top_bar") || null,
    benefitBar:
      barData?.data.bars.find((bar) => bar.name === "benefit_bar") || null
  };
};
